import { FALLIBLE, type NodeData, type NodeType, type StepStatus } from '@/lib/workflows'
import { cn } from '@/lib/utils'
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react'
import { AnimatePresence, motion, useMotionValue, useSpring } from 'framer-motion'
import { RotateCw } from 'lucide-react'
import { memo, useEffect, useRef, useState } from 'react'
import { AgentAvatar, type OrbState } from '../AgentAvatar'
import {
  BRANCHING,
  STEP_RING,
  TYPE_META,
  agentName,
  friendly,
  nodeSummary,
  nodeTitle,
  useCatalog,
  useLabels
} from './shared'

/** Graph data plus what the canvas paints on top: the selected run's status and "just added". */
export type FlowData = NodeData & {
  __status?: StepStatus
  __fresh?: boolean
  /** An agent is working on this step right now: who, what, and how (building, thinking, running). */
  __focus?: { agent: string; text: string; mode: 'build' | 'think' | 'run' | 'wait' }
}
export type FlowNode = Node<FlowData>

const handleStyle = {
  width: 12,
  height: 12,
  border: '2px solid var(--wf-handle-ring)',
  background: 'var(--wf-handle)'
}

const DOT: Record<StepStatus, string> = {
  running: 'bg-amber-500 animate-pulse',
  waiting: 'bg-amber-500',
  resumed: 'bg-amber-500 animate-pulse',
  done: 'bg-emerald-500',
  failed: 'bg-red-500',
  skipped: 'bg-neutral-400'
}

function avatarState(status?: StepStatus, fresh?: boolean): OrbState {
  if (status === 'running' || status === 'resumed') return 'working'
  if (status === 'waiting') return 'waiting'
  if (status === 'failed') return 'error'
  if (status === 'done') return 'replying'
  return fresh ? 'thinking' : 'idle'
}

const MAX_TILT = 6

/** While a card is dragged it swings with the pointer's speed, like something with weight, and
 * settles back when it stops. */
function useSwing(x: number, dragging: boolean) {
  const target = useMotionValue(0)
  const rotate = useSpring(target, { stiffness: 260, damping: 16, mass: 0.6 })
  const last = useRef({ x, t: 0 })
  useEffect(() => {
    const now = performance.now()
    const dt = Math.max(8, now - last.current.t)
    const speed = ((x - last.current.x) / dt) * 1000
    last.current = { x, t: now }
    if (!dragging) {
      target.set(0)
      return
    }
    target.set(Math.max(-MAX_TILT, Math.min(MAX_TILT, speed / 120)))
    const settle = window.setTimeout(() => target.set(0), 90)
    return () => window.clearTimeout(settle)
  }, [x, dragging, target])
  return rotate
}

/** Bumps when a step goes from working to finished, so each finished step gives one green pulse. */
function useFinishPulse(status?: StepStatus) {
  const [pulse, setPulse] = useState(0)
  const prev = useRef(status)
  useEffect(() => {
    const was = prev.current
    prev.current = status
    if ((status === 'done' || status === 'failed') && was && was !== status && was !== 'done')
      setPulse((p) => p + 1)
  }, [status])
  return pulse
}

function WorkflowNodeView({ id, type, data, selected, dragging, positionAbsoluteX }: NodeProps<FlowNode>) {
  const catalog = useCatalog()
  const labels = useLabels()
  const kind = type as NodeType
  const meta = TYPE_META[kind]
  const Icon = meta.icon
  const node = { id, type: kind, data }
  const withAgent = (kind === 'agent' || kind === 'action') && !!data.agent
  const branching = BRANCHING.includes(kind)
  const status = data.__status
  const focus = data.__focus
  const fallible = FALLIBLE.includes(kind)
  const retries = Number(data.retries) || 0
  const rotate = useSwing(positionAbsoluteX, !!dragging)
  const pulse = useFinishPulse(status)

  return (
    <motion.div
      initial={data.__fresh ? { opacity: 0, scale: 0.6, y: -28 } : false}
      animate={{ opacity: 1, scale: dragging ? 1.04 : 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 17, mass: 0.8 }}
      style={{ rotate }}
      className={cn(
        'relative w-[260px] rounded-[24px] border bg-white p-3.5 shadow-sm transition-shadow dark:bg-neutral-900',
        dragging && 'shadow-2xl',
        selected
          ? 'border-neutral-900 shadow-lg dark:border-white'
          : 'border-neutral-200 dark:border-neutral-800',
        status && STEP_RING[status],
        data.__fresh && !status && 'shadow-lg ring-2 ring-neutral-900/10 dark:ring-white/20',
        focus && 'ring-2 ring-amber-400 shadow-[0_0_0_6px_rgba(245,158,11,0.15)]'
      )}
    >
      {focus && (
        <motion.div
          key={`${focus.agent}-${focus.text}`}
          initial={{ opacity: 0, y: 6, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="pointer-events-none absolute -top-3 left-1/2 z-10 flex max-w-[300px] -translate-x-1/2 -translate-y-full items-center gap-2 whitespace-nowrap rounded-full border border-neutral-200 bg-white py-1 pl-1 pr-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-800"
        >
          <AgentAvatar
            agentKey={focus.agent}
            size="xs"
            state={focus.mode === 'think' ? 'thinking' : focus.mode === 'wait' ? 'waiting' : 'working'}
          />
          <span className="truncate text-xs font-semibold text-neutral-800 dark:text-neutral-100">
            {focus.text}
          </span>
          {focus.mode !== 'wait' && (
            <span className="flex shrink-0 gap-0.5">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="size-1 rounded-full bg-amber-500"
                  animate={{ opacity: [0.25, 1, 0.25] }}
                  transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1, delay: i * 0.18 }}
                />
              ))}
            </span>
          )}
          <span className="absolute -bottom-1.5 left-1/2 size-3 -translate-x-1/2 rotate-45 border-b border-r border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800" />
        </motion.div>
      )}
      <AnimatePresence>
        {pulse > 0 && (
          <motion.span
            key={pulse}
            initial={{ opacity: 0.55, scale: 1 }}
            animate={{ opacity: 0, scale: 1.18 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={cn(
              'pointer-events-none absolute inset-0 rounded-[24px] border-2',
              status === 'failed' ? 'border-red-500' : 'border-emerald-500'
            )}
          />
        )}
      </AnimatePresence>
      {kind !== 'trigger' && (
        <Handle type="target" position={Position.Top} style={handleStyle} />
      )}
      {fallible && (
        <Handle
          type="source"
          id="error"
          position={Position.Right}
          title="If it fails: drag from here to a fallback step"
          className="!bg-red-500"
          style={{ width: 10, height: 10, border: handleStyle.border }}
        />
      )}

      <div className="flex items-center gap-2">
        {withAgent ? (
          <AgentAvatar
            agentKey={data.agent}
            size="sm"
            state={avatarState(status, data.__fresh)}
          />
        ) : (
          <span
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-full',
              kind === 'trigger'
                ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200'
            )}
          >
            <Icon className="size-3.5" />
          </span>
        )}
        <span className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          {withAgent ? agentName(data.agent, catalog) : meta.label}
        </span>
        {retries > 0 && (
          <span
            className={cn(
              'ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
            )}
            title={`Tries again up to ${retries} time${retries === 1 ? '' : 's'}`}
          >
            <RotateCw className="size-2.5" />
            {retries}
          </span>
        )}
        {status && (
          <span
            className={cn('size-2 shrink-0 rounded-full', retries === 0 && 'ml-auto', DOT[status])}
            title={status}
          />
        )}
      </div>

      <p className="mt-2 line-clamp-2 text-sm font-bold leading-snug text-neutral-900 dark:text-white">
        {nodeTitle(node)}
      </p>
      <p className="mt-0.5 line-clamp-2 break-words text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
        {friendly(nodeSummary(node, catalog), labels)}
      </p>

      {branching ? (
        <>
          <Handle
            type="source"
            id="yes"
            position={Position.Bottom}
            style={{ ...handleStyle, left: '30%' }}
          />
          <Handle
            type="source"
            id="no"
            position={Position.Bottom}
            style={{ ...handleStyle, left: '70%' }}
          />
          <span className="pointer-events-none absolute -bottom-6 left-[30%] -translate-x-1/2 rounded-full bg-emerald-50 px-1.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
            {kind === 'classify' ? 'Found' : 'Yes'}
          </span>
          <span className="pointer-events-none absolute -bottom-6 left-[70%] -translate-x-1/2 rounded-full bg-neutral-100 px-1.5 text-[10px] font-bold text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            {kind === 'classify' ? 'None' : 'No'}
          </span>
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      )}
    </motion.div>
  )
}

export const WorkflowNode = memo(WorkflowNodeView)

export const nodeTypes = {
  trigger: WorkflowNode,
  agent: WorkflowNode,
  action: WorkflowNode,
  condition: WorkflowNode,
  approval: WorkflowNode,
  wait: WorkflowNode,
  notify: WorkflowNode,
  http: WorkflowNode,
  set: WorkflowNode,
  loop: WorkflowNode,
  classify: WorkflowNode
}
